import React, { useState, useEffect } from 'react';
/**
 * Background Schedulers for Crèche Compliance Monitoring
 * 
 * Runs automated compliance checks and generates alerts for:
 * - Predicted ratio breaches (next 3 hours)
 * - Funding insufficiency (this week) 
 * - Certificate expiries (DBS/Garda/FAR within 30 days)
 * 
 * Development: Runs every 5 minutes
 * Production: Configurable interval
 */

import { format, startOfWeek, endOfWeek } from 'date-fns';

// Store imports
import useComplianceStore from '../state/stores/compliance';
import useFundingStore from '../state/stores/funding';
import useStaffStore from '../state/stores/staff';
import useCentresStore from '../state/stores/centres';



// ============================================================================
// SCHEDULER CONFIGURATION
// ============================================================================

const SCHEDULER_CONFIG = {
  // Development: 5 minutes, Production: 15 minutes
  INTERVAL_MS: process.env?.NODE_ENV === 'development' ? 5 * 60 * 1000 : 15 * 60 * 1000,
  
  // Prediction lookahead windows
  BREACH_LOOKAHEAD_HOURS: 3,
  FUNDING_LOOKAHEAD_DAYS: 7,
  EXPIRY_WARNING_DAYS: 30,
  
  // Alert thresholds
  CRITICAL_STAFF_SHORTAGE: 2,
  WARNING_STAFF_SHORTAGE: 1,
  
  // Batch processing limits
  MAX_CENTRES_PER_BATCH: 10,
  MAX_PREDICTIONS_PER_RUN: 50
};

// ============================================================================
// ALERT TYPES AND SEVERITIES
// ============================================================================

export const AlertType = {
  RATIO_BREACH: 'ratio_breach',
  FUNDING_INSUFFICIENCY: 'funding_insufficiency',
  CERTIFICATE_EXPIRY: 'certificate_expiry',
  STAFF_SHORTAGE: 'staff_shortage',
  COMPLIANCE_VIOLATION: 'compliance_violation'
};

export const AlertSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// ============================================================================
// GLOBAL ALERT STORE
// ============================================================================

class AlertStore {
  constructor() {
    this.alerts = new Map();
    this.alertCount = 0;
    this.lastRunTimestamp = null;
    this.listeners = new Set();
  }

  addAlert(alert) {
    const id = `alert_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const alertWithId = {
      id,
      ...alert,
      timestamp: new Date()?.toISOString(),
      resolved: false,
      acknowledged: false
    };
    
    this.alerts?.set(id, alertWithId);
    this.alertCount++;
    this.notifyListeners();
    
    return alertWithId;
  }

  resolveAlert(alertId) {
    if (this.alerts?.has(alertId)) {
      const alert = this.alerts?.get(alertId);
      alert.resolved = true;
      alert.resolvedAt = new Date()?.toISOString();
      this.alerts?.set(alertId, alert);
      this.recalculateCount();
      this.notifyListeners();
    }
  }

  acknowledgeAlert(alertId) {
    if (this.alerts?.has(alertId)) {
      const alert = this.alerts?.get(alertId);
      alert.acknowledged = true;
      alert.acknowledgedAt = new Date()?.toISOString();
      this.alerts?.set(alertId, alert);
      this.notifyListeners();
    }
  }

  getActiveAlerts() {
    return Array.from(this.alerts?.values())?.filter(alert => !alert?.resolved);
  }

  getAlertsByType(type) {
    return this.getActiveAlerts()?.filter(alert => alert?.type === type);
  }

  getAlertCount() {
    return this.getActiveAlerts()?.length;
  }

  recalculateCount() {
    this.alertCount = this.getActiveAlerts()?.length;
  }

  clearResolvedAlerts() {
    const resolved = Array.from(this.alerts?.values())?.filter(alert => alert?.resolved);
    resolved?.forEach(alert => this.alerts?.delete(alert?.id));
    this.notifyListeners();
  }

  subscribe(callback) {
    this.listeners?.add(callback);
    return () => this.listeners?.delete(callback);
  }

  notifyListeners() {
    this.listeners?.forEach(callback => {
      try {
        callback({
          alerts: this.getActiveAlerts(),
          count: this.getAlertCount(),
          lastUpdate: new Date()?.toISOString()
        });
      } catch (error) {
        console.error('Alert listener error:', error);
      }
    });
  }

  setLastRunTimestamp(timestamp) {
    this.lastRunTimestamp = timestamp;
  }

  getLastRunTimestamp() {
    return this.lastRunTimestamp;
  }
}

// Global alert store instance
export const alertStore = new AlertStore();

// ============================================================================
// COMPLIANCE SCHEDULERS
// ============================================================================

class ComplianceScheduler {
  constructor() {
    this.isRunning = false;
    this.intervalId = null;
    this.runCount = 0;
  }

  start() {
    if (this.isRunning) {
      console.warn('Compliance scheduler is already running');
      return;
    }

    console.log(`Starting compliance scheduler - interval: ${SCHEDULER_CONFIG?.INTERVAL_MS / 1000}s`);
    
    // Run immediately on start
    this.runScheduledTasks()?.catch(error => {
      console.error('Initial scheduler run failed:', error);
    });

    // Set up recurring interval
    this.intervalId = setInterval(() => {
      this.runScheduledTasks()?.catch(error => {
        console.error('Scheduled task failed:', error);
      });
    }, SCHEDULER_CONFIG?.INTERVAL_MS);

    this.isRunning = true;
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
    console.log('Compliance scheduler stopped');
  }

  async runScheduledTasks() {
    const startTime = Date.now();
    this.runCount++;
    
    console.log(`🔄 Running compliance checks #${this.runCount} at ${new Date()?.toLocaleTimeString()}`);

    try {
      // Get current state from stores
      const complianceStore = useComplianceStore?.getState();
      const fundingStore = useFundingStore?.getState();
      const staffStore = useStaffStore?.getState();
      const centresStore = useCentresStore?.getState();

      // Run all scheduled checks in parallel
      const results = await Promise.allSettled([
        this.checkRatioBreaches(complianceStore, centresStore),
        this.checkFundingInsufficiency(fundingStore, centresStore),
        this.checkCertificateExpiries(complianceStore, staffStore),
        this.cleanupOldAlerts()
      ]);

      // Process results and log any failures
      results?.forEach((result, index) => {
        const taskNames = ['Ratio Breaches', 'Funding Issues', 'Certificate Expiries', 'Cleanup'];
        if (result?.status === 'rejected') {
          console.error(`${taskNames?.[index]} check failed:`, result?.reason);
        }
      });

      const duration = Date.now() - startTime;
      alertStore?.setLastRunTimestamp(new Date()?.toISOString());
      
      console.log(`✅ Compliance checks completed in ${duration}ms - Active alerts: ${alertStore?.getAlertCount()}`);

    } catch (error) {
      console.error('Scheduler run failed:', error);
    }
  }

  /**
   * Check for predicted ratio breaches in the next 3 hours
   */
  async checkRatioBreaches(complianceStore, centresStore) {
    try {
      const centres = Array.from(centresStore?.centres?.values());
      const today = format(new Date(), 'yyyy-MM-dd');
      let totalPredictions = 0;

      for (const centre of centres?.slice(0, SCHEDULER_CONFIG?.MAX_CENTRES_PER_BATCH)) {
        try {
          const predictions = complianceStore?.predictBreach?.(
            centre?.id, 
            today, 
            SCHEDULER_CONFIG?.BREACH_LOOKAHEAD_HOURS
          );

          if (predictions?.length > 0) {
            predictions?.forEach(prediction => {
              if (totalPredictions < SCHEDULER_CONFIG?.MAX_PREDICTIONS_PER_RUN) {
                this.createRatioBreachAlert(prediction, centre);
                totalPredictions++;
              }
            });
          }
        } catch (error) {
          console.error(`Ratio breach check failed for centre ${centre?.name}:`, error);
        }
      }

      if (totalPredictions > 0) {
        console.log(`🚨 Found ${totalPredictions} predicted ratio breaches`);
      }

    } catch (error) {
      console.error('Ratio breach monitoring failed:', error);
    }
  }

  /**
   * Check for funding insufficiency this week
   */
  async checkFundingInsufficiency(fundingStore, centresStore) {
    try {
      const centres = Array.from(centresStore?.centres?.values());
      const weekStart = startOfWeek(new Date());
      const weekEnd = endOfWeek(new Date());

      for (const centre of centres) {
        try {
          // Check funding calculations for the current week
          const weeklyFunding = fundingStore?.calculateWeeklyFunding?.(centre?.id, weekStart, weekEnd);
          
          if (weeklyFunding) {
            const shortfall = weeklyFunding?.required - weeklyFunding?.available;
            
            if (shortfall > 0) {
              let severity = shortfall > weeklyFunding?.required * 0.3 
                ? AlertSeverity?.CRITICAL 
                : AlertSeverity?.HIGH;

              this.createFundingAlert({
                centreId: centre?.id,
                centreName: centre?.name,
                shortfall,
                weeklyFunding,
                severity,
                weekPeriod: { start: weekStart, end: weekEnd }
              });
            }
          }
        } catch (error) {
          console.error(`Funding check failed for centre ${centre?.name}:`, error);
        }
      }
    } catch (error) {
      console.error('Funding insufficiency check failed:', error);
    }
  }

  /**
   * Check for certificate expiries (DBS/Garda/FAR) within 30 days
   */
  async checkCertificateExpiries(complianceStore, staffStore) {
    try {
      const expiringCerts = complianceStore?.getExpiringCertifications?.(
        SCHEDULER_CONFIG?.EXPIRY_WARNING_DAYS
      );

      if (expiringCerts?.length > 0) {
        const staff = Array.from(staffStore?.staff?.values());
        
        expiringCerts?.forEach(cert => {
          const staffMember = staff?.find(s => s?.id === cert?.staffId);
          if (staffMember) {
            this.createExpiryAlert(cert, staffMember);
          }
        });

        console.log(`📋 Found ${expiringCerts?.length} expiring certificates`);
      }
    } catch (error) {
      console.error('Certificate expiry check failed:', error);
    }
  }

  /**
   * Create ratio breach alert
   */
  createRatioBreachAlert(prediction, centre) {
    // Check if similar alert already exists
    const existingAlerts = alertStore?.getAlertsByType(AlertType?.RATIO_BREACH);
    const duplicate = existingAlerts?.find(alert => 
      alert?.centreId === centre?.id && 
      alert?.roomId === prediction?.roomId &&
      alert?.timeSlot === prediction?.timeSlot
    );

    if (duplicate) return; // Don't create duplicate alerts

    let severity = prediction?.severity === 'critical' 
      ? AlertSeverity?.CRITICAL 
      : AlertSeverity?.HIGH;

    alertStore?.addAlert({
      type: AlertType?.RATIO_BREACH,
      severity,
      title: 'Predicted Ratio Breach',
      message: `${prediction?.roomName} will breach ratio requirements at ${prediction?.timeSlot}`,
      details: {
        centreId: centre?.id,
        centreName: centre?.name,
        roomId: prediction?.roomId,
        roomName: prediction?.roomName,
        timeSlot: prediction?.timeSlot,
        prediction: prediction?.prediction,
        recommendedActions: prediction?.recommendedActions
      },
      actions: [
        {
          label: 'View Rota',
          action: 'navigate',
          target: '/creche/rota',
          params: { centreId: centre?.id, roomId: prediction?.roomId }
        },
        {
          label: 'Add Staff',
          action: 'modal',
          target: 'staff-assignment'
        }
      ]
    });
  }

  /**
   * Create funding insufficiency alert
   */
  createFundingAlert(fundingIssue) {
    // Check for existing funding alert for this centre this week
    const existingAlerts = alertStore?.getAlertsByType(AlertType?.FUNDING_INSUFFICIENCY);
    const duplicate = existingAlerts?.find(alert => 
      alert?.centreId === fundingIssue?.centreId &&
      alert?.weekStart === format(fundingIssue?.weekPeriod?.start, 'yyyy-MM-dd')
    );

    if (duplicate) return;

    alertStore?.addAlert({
      type: AlertType?.FUNDING_INSUFFICIENCY,
      severity: fundingIssue?.severity,
      title: 'Weekly Funding Shortfall',
      message: `${fundingIssue?.centreName} has £${fundingIssue?.shortfall?.toFixed(2)} funding shortfall this week`,
      details: {
        centreId: fundingIssue?.centreId,
        centreName: fundingIssue?.centreName,
        shortfall: fundingIssue?.shortfall,
        weeklyFunding: fundingIssue?.weeklyFunding,
        weekStart: format(fundingIssue?.weekPeriod?.start, 'yyyy-MM-dd'),
        weekEnd: format(fundingIssue?.weekPeriod?.end, 'yyyy-MM-dd')
      },
      actions: [
        {
          label: 'View Funding',
          action: 'navigate',
          target: '/creche/funding',
          params: { centreId: fundingIssue?.centreId }
        },
        {
          label: 'Request Additional Funding',
          action: 'modal',
          target: 'funding-request'
        }
      ]
    });
  }

  /**
   * Create certificate expiry alert
   */
  createExpiryAlert(certificate, staffMember) {
    const daysUntilExpiry = Math.ceil(
      (new Date(certificate.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    let severity = AlertSeverity?.LOW;
    if (daysUntilExpiry <= 7) severity = AlertSeverity?.CRITICAL;
    else if (daysUntilExpiry <= 14) severity = AlertSeverity?.HIGH;
    else if (daysUntilExpiry <= 21) severity = AlertSeverity?.MEDIUM;

    const certTypeNames = {
      dbs_garda: 'DBS/Garda Vetting',
      first_aid: 'First Aid Certificate',
      safeguarding: 'Safeguarding Training',
      food_hygiene: 'Food Hygiene Certificate',
      qualification: 'Professional Qualification',
      manual_handling: 'Manual Handling Training'
    };

    alertStore?.addAlert({
      type: AlertType?.CERTIFICATE_EXPIRY,
      severity,
      title: 'Certificate Expiring Soon',
      message: `${staffMember?.firstName} ${staffMember?.lastName}'s ${certTypeNames?.[certificate?.type]} expires in ${daysUntilExpiry} days`,
      details: {
        staffId: staffMember?.id,
        staffName: `${staffMember?.firstName} ${staffMember?.lastName}`,
        certificateType: certificate?.type,
        certificateTypeName: certTypeNames?.[certificate?.type],
        expiryDate: certificate?.expiryDate,
        daysUntilExpiry
      },
      actions: [
        {
          label: 'Renew Certificate',
          action: 'modal',
          target: 'certificate-renewal',
          params: { staffId: staffMember?.id, certType: certificate?.type }
        },
        {
          label: 'View Staff Profile',
          action: 'navigate',
          target: '/staff-profile',
          params: { staffId: staffMember?.id }
        }
      ]
    });
  }

  /**
   * Clean up old resolved alerts
   */
  async cleanupOldAlerts() {
    try {
      const cutoffTime = new Date();
      cutoffTime?.setDate(cutoffTime?.getDate() - 7); // Remove alerts older than 7 days

      const alerts = Array.from(alertStore?.alerts?.values());
      let removedCount = 0;

      alerts?.forEach(alert => {
        if (alert?.resolved && new Date(alert.resolvedAt) < cutoffTime) {
          alertStore?.alerts?.delete(alert?.id);
          removedCount++;
        }
      });

      if (removedCount > 0) {
        alertStore?.notifyListeners();
        console.log(`🗑️ Cleaned up ${removedCount} old alerts`);
      }
    } catch (error) {
      console.error('Alert cleanup failed:', error);
    }
  }

  getStatus() {
    return {
      isRunning: this.isRunning,
      runCount: this.runCount,
      lastRun: alertStore?.getLastRunTimestamp(),
      activeAlerts: alertStore?.getAlertCount(),
      interval: SCHEDULER_CONFIG?.INTERVAL_MS
    };
  }
}

// ============================================================================
// SINGLETON SCHEDULER INSTANCE
// ============================================================================

export const complianceScheduler = new ComplianceScheduler();

// ============================================================================
// REACT HOOKS FOR ALERT INTEGRATION
// ============================================================================

export const useAlerts = () => {
  const [alerts, setAlerts] = React.useState(alertStore?.getActiveAlerts());
  const [alertCount, setAlertCount] = React.useState(alertStore?.getAlertCount());

  React.useEffect(() => {
    const unsubscribe = alertStore?.subscribe((data) => {
      setAlerts(data?.alerts);
      setAlertCount(data?.count);
    });

    return unsubscribe;
  }, []);

  return {
    alerts,
    alertCount,
    resolveAlert: (alertId) => alertStore?.resolveAlert(alertId),
    acknowledgeAlert: (alertId) => alertStore?.acknowledgeAlert(alertId),
    getAlertsByType: (type) => alertStore?.getAlertsByType(type),
    getAlertsBySeverity: (severity) => alerts?.filter(alert => alert?.severity === severity)
  };
};

// ============================================================================
// SCHEDULER MANAGEMENT UTILITIES
// ============================================================================

export const startComplianceScheduler = () => {
  if (typeof window !== 'undefined') { // Client-side only
    complianceScheduler?.start();
  }
};

export const stopComplianceScheduler = () => {
  complianceScheduler?.stop();
};

export const getSchedulerStatus = () => {
  return complianceScheduler?.getStatus();
};

// Auto-start scheduler in development
if (process.env?.NODE_ENV === 'development' && typeof window !== 'undefined') {
  // Delay start to ensure stores are initialized
  setTimeout(() => {
    startComplianceScheduler();
    console.log('🚀 Compliance scheduler auto-started in development mode');
  }, 2000);
}

// ============================================================================
// MANUAL TESTING UTILITIES (Development Only)
// ============================================================================

if (process.env?.NODE_ENV === 'development') {
  window.complianceScheduler = {
    start: startComplianceScheduler,
    stop: stopComplianceScheduler,
    status: getSchedulerStatus,
    runNow: () => complianceScheduler?.runScheduledTasks(),
    alertStore,
    createTestAlert: (type = 'test') => {
      alertStore?.addAlert({
        type: AlertType?.RATIO_BREACH,
        severity: AlertSeverity?.HIGH,
        title: 'Test Alert',
        message: 'This is a test alert for development',
        details: { test: true }
      });
    }
  };
}